import type { ReactNode } from "react";
import { Sparkle, ThumbsUp } from "@phosphor-icons/react";
import type { AudienceCategory, AudienceReply, RoomThread } from "../../shared/types.ts";
import { authorDisplay, categoryLabel, fmtAgo, replyAuthorDisplay } from "../lib/threads.ts";

export function SelfAttributionMark() {
  return <span className="you-mark" data-testid="self-attribution">You</span>;
}

export function RoomThreadCard({
  thread,
  categories,
  hero = false,
  onAir = false,
  actionsRail,
  metaLike = false,
  ownMark = false,
  ownParticipantKey,
  footerLeft,
  footerRight,
  bodyContent,
  replyComposer,
  showReplies,
  replies,
  renderReplyAction,
  showStaleSource = false,
}: {
  thread: RoomThread;
  categories: AudienceCategory[];
  hero?: boolean;
  onAir?: boolean;
  actionsRail?: ReactNode;
  metaLike?: boolean;
  ownMark?: boolean;
  ownParticipantKey?: string | null;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  bodyContent?: ReactNode;
  replyComposer?: ReactNode;
  showReplies: boolean;
  replies?: AudienceReply[];
  renderReplyAction?: (reply: AudienceReply) => ReactNode;
  showStaleSource?: boolean;
}) {
  const author = authorDisplay(thread);
  const showAuthor = author.kind !== "hidden" && Boolean(author.label);
  const cat = categoryLabel(thread, categories);
  const visibleReplies = showReplies ? (replies ?? thread.replies ?? []) : [];
  const hasFooter = footerLeft || footerRight;

  const content = (
    <>
      {hero && (
        <div className="hero-kicker">
          On screen now <span className="when">{fmtAgo(thread.lastActivityAt)}</span>
        </div>
      )}
      <div className="pt-meta">
        {showAuthor && (
          <span className={`who ${author.kind === "ai" ? "ai" : ""} ${author.kind === "anonymous" ? "anon" : ""}`}>
            {author.kind === "ai" && <Sparkle weight="fill" />} {author.label}
          </span>
        )}
        {ownMark && <SelfAttributionMark />}
        {cat && <>{(showAuthor || ownMark) && <span className="dot-sep">·</span>}{cat}</>}
        {metaLike && thread.likeCount > 0 && <><span className="dot-sep">·</span><span className="meta-like"><ThumbsUp weight="bold" /> {thread.likeCount}</span></>}
        {thread.status === "queued" && <><span className="dot-sep">·</span><span className="badge status status-queued">pending</span></>}
        {thread.status === "answered" && <><span className="dot-sep">·</span><span className="badge status status-answered">answered</span></>}
        {showStaleSource && thread.staleSourceAt && <><span className="dot-sep">·</span><span className="badge status status-stale-source" data-testid="thread-stale-source" title="The source transcript was corrected after this question was generated.">source corrected</span></>}
        {!hero && <span className="when">{fmtAgo(thread.createdAt)}</span>}
      </div>
      <div className="pt-body room-thread-body">{bodyContent ?? thread.body}</div>
      {hasFooter && (
        <div className="pt-actions">
          {footerLeft ?? <span />}
          {footerRight}
        </div>
      )}
      {visibleReplies.length > 0 && (
        <div className="pt-replies-wrap">
          <div className="pt-replies">
            {visibleReplies.map((reply) => {
              const replyAuthor = replyAuthorDisplay(
                reply.authorLabel,
                reply.authorIdentityKind,
              );
              const ownReply = Boolean(ownParticipantKey && reply.authorKey === ownParticipantKey);
              return (
                <div className={`pt-reply ${ownReply ? "is-own" : ""}`} key={reply.id}>
                  {renderReplyAction && <span className="pt-reply-like">{renderReplyAction(reply)}</span>}
                  <span className="reply-text">
                    {replyAuthor.kind !== "hidden" && replyAuthor.label && (
                      <><span className={`who ${replyAuthor.kind === "anonymous" ? "anon" : ""}`}>{replyAuthor.label}</span>{" "}</>
                    )}
                    {ownReply && <><SelfAttributionMark />{" "}</>}
                    {reply.body}
                  </span>
                  {reply.status === "queued" && <span className="tag">awaiting approval</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {replyComposer}
    </>
  );

  return (
    <article
      className={`pt-card ${hero ? "hero" : ""} ${thread.threadOrigin === "ai" ? "is-ai" : ""} ${ownMark ? "is-own" : ""} ${onAir ? "is-onair" : ""} ${actionsRail ? "has-rail" : ""} ${thread.status === "answered" ? "is-answered" : ""}`}
      data-testid="room-thread-card"
      data-thread-kind={thread.threadKind}
      data-status={thread.status}
    >
      {actionsRail ? (
        <>
          <div className="pt-content">{content}</div>
          <div className="pt-rail">{actionsRail}</div>
        </>
      ) : content}
    </article>
  );
}
